<template>
  <el-tooltip>
    <!-- Popup text status -->
    <span slot="content">
      <div class="status-tooltip">
        <span>{{ getTextStatus }}</span>
        <close-icon
          v-if="social !== 'mangomolo'"
          class="remove-sharing-icon"
          title="Remove sharing information"
          @click="confirmRemoveSharing"
        />
      </div>
    </span>
    <!-- Status icon -->
    <div :style="iconContainerStyle">
      <div :class="iconClass">
        <a :href="link" target="_blank">
          <img :style="iconStyle" :src="iconSrc" />
        </a>
      </div>
    </div>
  </el-tooltip>
</template>

<script>
import { SOCIAL_SHARING_STATUS, isSharingStatusValid } from "@/constants.js";
import { getDateAndTimeLine } from "@/utils/time.js";

/** icons */
import CloseIcon from "vue-material-design-icons/Close.vue";
import youtubeIcon from "@/assets/images/youtube_sharing.png";
import facebookIcon from "@/assets/images/facebook_sharing.png";
import instagramIcon from "@/assets/images/instagram_sharing.png";
import twitterIcon from "@/assets/images/twitter_sharing.png";
import mangomoloIcon from "@/assets/images/mangomolo.png";
import mangomoloLoadedIcon from "@/assets/images/mangomolo_loaded.png";

import dayjs from "dayjs";

// @group Socials
// Status icon of shared video
export default {
  name: "SharingStatusIcon",
  components: {
    CloseIcon
  },
  props: {
    // Social (youtube/facebook/instagram/twitter/mangomolo)
    social: {
      type: String,
      default: "youtube",
      validator(val) {
        const availableSocials = [
          "youtube",
          "facebook",
          "instagram",
          "twitter",
          "mangomolo"
        ];
        return availableSocials.includes(val);
      }
    },
    // Converting status (error/enqueued/processing/ready)
    status: {
      type: String,
      default: SOCIAL_SHARING_STATUS,
      validator(val) {
        return isSharingStatusValid(val);
      }
    },
    // Shared video link
    link: {
      type: String,
      default: null
    },
    // Percentage of converting
    percentage: {
      type: Number,
      default: 0
    },
    // Icon width & height in pixels
    size: {
      type: Number,
      default: 26
    },
    // When sharing will/was be done
    publishTime: {
      type: Number,
      default: null
    },
    // Sharing time timezone
    timezone: {
      type: String,
      default: dayjs.tz.guess()
    }
  },
  paddingsWidth: 3,
  computed: {
    // @vuese
    // Social icon (youtube/facebook/instagram/twitter/mangomolo)
    iconSrc() {
      switch (this.social) {
        case "youtube":
          return youtubeIcon;
        case "facebook":
          return facebookIcon;
        case "instagram":
          return instagramIcon;
        case "twitter":
          return twitterIcon;
        case "mangomolo":
          return this.status == SOCIAL_SHARING_STATUS.READY
            ? mangomoloLoadedIcon
            : mangomoloIcon;
        default:
          return null;
      }
    },
    // @vuese
    // Icon image width in pixels
    iconSize() {
      return this.size - this.$options.paddingsWidth * 2;
    },
    // @vuese
    // Icon status class
    iconClass() {
      switch (this.status) {
        case SOCIAL_SHARING_STATUS.READY:
          return "converted-link";
        case SOCIAL_SHARING_STATUS.ENQUEUED:
          return "enqueued-link";
        case SOCIAL_SHARING_STATUS.PROCESSING:
          return "process-link";
        case SOCIAL_SHARING_STATUS.UPLOADING:
          return "process-link";
        case SOCIAL_SHARING_STATUS.ERROR:
          return "error-link";
        default:
          return "";
      }
    },
    // @vuese
    // Status label
    getTextStatus() {
      switch (this.status) {
        case SOCIAL_SHARING_STATUS.READY:
          if (this.social == "mangomolo") {
            return "Uploaded";
          }
          return "Shared";
        case SOCIAL_SHARING_STATUS.ENQUEUED:
          if (this.publishTime) {
            const time = getDateAndTimeLine(this.publishTime, this.timezone);
            return `Scheduled at ${time} (${this.timezone})`;
          } else {
            return "Enqueued";
          }
        case SOCIAL_SHARING_STATUS.UPLOADING:
          return `${Math.round(Number(this.percentage))}%`;
        case SOCIAL_SHARING_STATUS.PROCESSING:
          if (this.social == "mangomolo") {
            return "Uploading...";
          }
          return "Converting...";
        case SOCIAL_SHARING_STATUS.ERROR:
          return "Failed";
        default:
          console.log(`Sharing status "${this.status}" is not defined.`);
          // return "Unknown";
          return this.status;
      }
    },
    // @vuese
    // icon image width & height CSS style object
    iconStyle() {
      return {
        width: this.iconSize + "px",
        height: this.iconSize + "px"
      };
    },
    // @vuese
    // icon block width & height & paddings CSS style object
    iconContainerStyle() {
      return {
        "min-width": this.iconSize + "px",
        height: this.iconSize + "px",
        padding: this.$options.paddingsWidth + "px"
      };
    }
  },
  methods: {
    // @vuese
    // Open remove confirmation form
    confirmRemoveSharing() {
      this.$confirm(
        `Do you want to remove video sharing data (it won't remove shared video in social, only sharing icon)?`,
        "Remove Sharing",
        {
          confirmButtonText: "Remove",
          cancelButtonText: "Cancel"
        }
      ).then(() => {
        // Status removed by user
        this.$emit("removed");
      });
    }
  }
};
</script>

<style scoped>
.enqueued-link {
  opacity: 0.5;
}
.converted-link img {
  cursor: pointer !important;
}
.process-link {
  /*animation: load-sharing 2s infinite;*/
  overflow: hidden;
  animation-duration: 1.5s;
  animation-name: load-sharing;
  animation-iteration-count: infinite;
  animation-direction: normal;
}
.error-link img {
  opacity: 1;
  border: none;
  clip-path: polygon(
    20% 0%,
    0% 20%,
    30% 50%,
    0% 80%,
    20% 100%,
    50% 70%,
    80% 100%,
    100% 80%,
    70% 50%,
    100% 20%,
    80% 0%,
    50% 30%
  );
  background-size: 100% 100%;
}

.status-tooltip {
  display: flex;
}

.remove-sharing-icon .material-design-icon__svg {
  height: 14px;
  width: 14px;
  margin-left: 3px;
}
</style>
